import { cn } from "@/lib/utils";

interface LoadingSpinnerProps {
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
  message?: string;
  variant?: "spinner" | "dots" | "pulse";
}

const sizeClasses = {
  sm: "w-5 h-5 border-2",
  md: "w-8 h-8 border-3",
  lg: "w-12 h-12 border-4",
  xl: "w-16 h-16 border-4",
};

const dotSizeClasses = {
  sm: "w-1.5 h-1.5",
  md: "w-2 h-2",
  lg: "w-3 h-3",
  xl: "w-4 h-4",
};

const LoadingSpinner = ({ 
  size = "md", 
  className, 
  message,
  variant = "spinner" 
}: LoadingSpinnerProps) => {
  if (variant === "dots") {
    return (
      <div className={cn("flex flex-col items-center justify-center gap-3", className)}>
        <div className="flex items-center gap-1.5">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className={cn(
                "rounded-full bg-pop animate-bounce",
                dotSizeClasses[size]
              )}
              style={{ animationDelay: `${i * 0.15}s` }}
            />
          ))}
        </div>
        {message && (
          <p className="text-sm text-muted-foreground animate-fade-in">{message}</p>
        )}
      </div>
    );
  }

  if (variant === "pulse") {
    return (
      <div className={cn("flex flex-col items-center justify-center gap-3", className)}>
        <div className="relative">
          <div className={cn(
            "rounded-full bg-pop/30",
            sizeClasses[size].replace("border-", "")
          )} />
          <div className={cn(
            "absolute inset-0 rounded-full bg-pop/50 animate-ping",
            sizeClasses[size].replace("border-", "")
          )} />
        </div>
        {message && (
          <p className="text-sm text-muted-foreground animate-fade-in">{message}</p>
        )}
      </div>
    );
  }

  return (
    <div className={cn("flex flex-col items-center justify-center", className)}>
      <div
        className={cn(
          "rounded-full border-pop border-t-transparent animate-spin",
          sizeClasses[size]
        )}
      />
      {message && (
        <p className="mt-3 text-sm text-muted-foreground animate-fade-in">{message}</p>
      )}
    </div>
  );
};

export default LoadingSpinner;
